import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import { useMobileApp } from '../utils/MobileAppContext';
import { TypeAnimation } from 'react-type-animation';
import type { ChangeEvent, FormEvent } from 'react';
import {
  Mail,
  Clock,
  Send,
  CheckCircle,
  ChevronDown,
  MessageSquare,
  Users,
  Zap,
  MapPin,
  Map
} from 'lucide-react';
import emailjs from '@emailjs/browser';
import EMAILJS_CONFIG from '../config/Config';
import Loader from "../components/Loader";
import { API_BASE_URL } from '../utils/api';
import Swal from 'sweetalert2';

const Contact: React.FC = () => {
  const { setHighlight, isHighlighted } = useMobileApp();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    service: '',
    budget: '',
    message: ''
  });
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const [isBudgetDropdownOpen, setIsBudgetDropdownOpen] = useState(false);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setHighlight(location.pathname, 'contact-submit-btn');
    setLoading(true);
    setStatusMessage("");

    // 1. Save to Database
    try {
      const response = await fetch(`${API_BASE_URL}/leads/public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-web-api-key': 'devnectar-public-key-123' // Matching the backend key
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to save lead to database');
      }
    } catch (err) {
      console.error('Error saving lead:', err);
      setStatusMessage("❌ System error. Please try again later.");
      setLoading(false);
      return; // Stop execution if DB save fails
    }

    // 2. Send Email (Only if DB save was successful)
    emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID,
      formData,
      EMAILJS_CONFIG.PUBLIC_KEY
    )
      .then(() => {
        setStatusMessage("✅ Email sent successfully! Thanks for contacting us. We will get back to you soon.");
        setLoading(false);
        setFormData({ name: '', email: '', phone: '', company_name: '', service: '', budget: '', message: '' });
        (e.target as HTMLFormElement).reset();
        Swal.fire({
          icon: 'success',
          title: 'Thank You!',
          text: "Thank you for your message! We'll get back to you soon.",
          confirmButtonColor: '#0f766e',
          customClass: {
            popup: 'rounded-2xl',
            confirmButton: 'rounded-xl px-6 py-2.5 font-medium'
          }
        });
      }, () => {
        // Even if email fails, we saved the lead, so we can consider it a partial success or just warn
        console.error("Email failed to send");
        setStatusMessage("✅ Request received! (Email notification failed, but we have your details)");
        setLoading(false);
        setFormData({ name: '', email: '', phone: '', company_name: '', service: '', budget: '', message: '' });
        (e.target as HTMLFormElement).reset();
        Swal.fire({
          icon: 'success',
          title: 'Request Received!',
          text: "Thank you! We have received your request and will contact you shortly.",
          confirmButtonColor: '#0f766e',
          customClass: {
            popup: 'rounded-2xl',
            confirmButton: 'rounded-xl px-6 py-2.5 font-medium'
          }
        });
      });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      content: 'sales@devnectar.in',
      description: 'Send us an email anytime'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Mon - Fri: 9AM - 6PM IST',
      description: 'Weekend support available'
    },
    {
      icon: MapPin,
      title: 'Our Office',
      content: 'Office No. 827, 8th Floor, Suratwala Mark Plazzo, Hinjewadi Phase-1, Pune - 411057',
      description: 'Visit us or send mail',
      mapLink: 'https://maps.app.goo.gl/K1tg2ySdGbg7QHtJA'
    }
  ];

  const services = [
    'Web Development',
    'Mobile App Development',
    'Azure/AWS Cloud Solutions',
    'M365 Development',
    'Database Design',
    'DevOps Services',
    'Technical Consulting',
    'Digital Marketing',
    'Corporate Training',
    'Sub Contracting',
    '3D scientific visualization',
    'Other'
  ];

  const budgetOptions = [
    { label: '< $5,000', value: '< $5k' },
    { label: '$5,000 - $10,000', value: '$5k - $10k' },
    { label: '$10,000 - $25,000', value: '$10k - $25k' },
    { label: '$25,000+', value: '$25k+' }
  ];

  const faqs = [
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary based on complexity and scope. Simple web applications typically take 4-8 weeks, while complex enterprise solutions can take 3-6 months. We provide detailed timelines during our initial consultation.'
    },
    {
      question: 'What technologies do you work with?',
      answer: 'We work with all modern technologies including Sharepoint, Power Platform, Azure, React, Node.js, Python, AWS, Docker, PostgreSQL, MongoDB, React Native, Flutter, and many more. Our team stays updated with the latest tech trends.'
    },
    {
      question: 'Do you provide ongoing support?',
      answer: 'Yes! We offer comprehensive support packages including bug fixes, updates, monitoring, and feature enhancements. Our support plans range from 30 days to ongoing monthly maintenance contracts.'
    },
    {
      question: 'Can you work with our existing team?',
      answer: 'Absolutely! We excel at collaborative development and can seamlessly integrate with your existing team, processes, and tools. We adapt to your workflow and communication preferences.'
    },
    {
      question: 'What is your development process?',
      answer: 'We follow an agile methodology with regular sprints, daily standups, and continuous client communication. Our process includes discovery, planning, development, testing, and deployment phases with regular feedback loops.'
    },
    {
      question: 'How do you ensure project quality?',
      answer: 'We maintain high quality through code reviews, automated testing, continuous integration, security audits, and performance optimization. Every project follows our quality checklist before delivery.'
    }
  ];

  const features = [
    {
      icon: MessageSquare,
      title: 'Free Consultation',
      description: 'Get expert advice on your project requirements and technology choices at no cost.'
    },
    {
      icon: Users,
      title: 'Dedicated Team',
      description: 'Work with experienced developers committed to your project\'s success.'
    },
    {
      icon: Zap,
      title: 'Quick Response',
      description: 'We respond to all inquiries within 24 hours, usually much faster.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Contact Us"
        description="Ready to start your project? Get in touch with our team of experts for a free consultation."
        keywords="Contact Software Developers, Hire React Developers, SharePoint Consulting, Free Tech Consultation"
      />
      {/* Hero Section */}
      <section data-id="contact-hero" className="bg-gradient-to-br from-blue-900 via-blue-900 to-indigo-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 data-id="contact-hero-title" className="text-4xl md:text-6xl font-bold mb-6 flex flex-col items-center gap-4 animate-fade-in-up">
              <span>Let's Build</span>
              <span className="inline-block pb-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 animate-gradient-x">
                <TypeAnimation
                  sequence={[
                    'Something Amazing',
                    2000,
                    'Something Innovative',
                    2000,
                    'Something Transformative',
                    2000,
                  ]}
                  wrapper="span"
                  speed={50}
                  repeat={Infinity}
                />
              </span>
            </h1>
            <p data-id="contact-hero-description" className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Ready to start your project? Get in touch with our team of experts.
              We're here to help you turn your ideas into reality.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section data-id="contact-features" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} data-id={`contact-feature-${index}`} className="text-center group hover:-translate-y-2 transition-transform duration-300 cursor-default">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-xl mb-5 group-hover:bg-blue-600 group-hover:shadow-lg group-hover:shadow-blue-300 transition-all duration-300">
                    <IconComponent className="h-7 w-7 text-blue-600 group-hover:text-white transition-colors duration-300 group-hover:animate-bounce" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section data-id="main-contact" className="pt-20 pb-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div data-id="contact-form-section">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Get Started Today</h2>
              <form data-id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder=""
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder=""
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="number"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder=""
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Needed *
                    </label>
                    <button
                      type="button"
                      id="service"
                      onClick={() => setIsServiceDropdownOpen(!isServiceDropdownOpen)}
                      className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-left"
                    >
                      <span className={formData.service ? 'text-gray-900' : 'text-gray-400'}>
                        {formData.service || "Select a service"}
                      </span>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isServiceDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isServiceDropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setIsServiceDropdownOpen(false)}
                        />
                        <div className="absolute z-50 w-full mt-2 bg-white/90 backdrop-blur-md border border-gray-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto animate-fade-in-up py-2">
                          {services.map((service, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, service });
                                setIsServiceDropdownOpen(false);
                              }}
                              className={`w-full px-4 py-3 text-left transition-colors flex items-center justify-between ${formData.service === service
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                              {service}
                              {formData.service === service && (
                                <CheckCircle className="h-4 w-4 text-blue-600" />
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="relative">
                    <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                      Estimated Budget
                    </label>
                    <button
                      type="button"
                      id="budget"
                      onClick={() => setIsBudgetDropdownOpen(!isBudgetDropdownOpen)}
                      className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white text-left"
                    >
                      <span className={formData.budget ? 'text-gray-900' : 'text-gray-400'}>
                        {budgetOptions.find(opt => opt.value === formData.budget)?.label || "Select a range"}
                      </span>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isBudgetDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isBudgetDropdownOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setIsBudgetDropdownOpen(false)}
                        />
                        <div className="absolute z-50 w-full mt-2 bg-white/90 backdrop-blur-md border border-gray-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto animate-fade-in-up py-2">
                          {budgetOptions.map((option, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, budget: option.value });
                                setIsBudgetDropdownOpen(false);
                              }}
                              className={`w-full px-4 py-3 text-left transition-colors flex items-center justify-between ${formData.budget === option.value
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                              {option.label}
                              {formData.budget === option.value && (
                                <CheckCircle className="h-4 w-4 text-blue-600" />
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder=""
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                    placeholder="Tell us about your project requirements, goals, and any specific features you need..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  data-id="contact-submit-btn"
                  className={`w-full inline-flex items-center justify-center px-8 py-4 font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl ${
                    isHighlighted(location.pathname, 'contact-submit-btn')
                      ? 'bg-blue-700 ring-4 ring-blue-500/50 scale-[1.02]'
                      : 'bg-gradient-to-r from-blue-600 to-blue-600 text-white hover:from-blue-700 hover:to-blue-700'
                  } text-white`}
                >
                  Send Message
                  <Send className="ml-2 h-5 w-5" />
                </button>
              </form>
              {loading && <Loader />}
              {statusMessage && <p>{statusMessage}</p>}
            </div>

            {/* Contact Information */}
            <div data-id="contact-info-section">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Get in Touch</h2>
              <div className="space-y-8">
                {contactInfo.map((info, index) => {
                  const IconComponent = info.icon;
                  return (
                    <div key={index} data-id={`contact-info-${index}`} className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-600 rounded-lg">
                          <IconComponent className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{info.title}</h3>
                        <p className={`${info.title === 'Our Office' ? 'text-gray-900' : 'text-gray-900 font-semibold'} mb-1`}>{info.content}</p>
                        <p className="text-gray-600 text-sm mb-1">{info.description}</p>
                        {info.mapLink && (
                          <div className="mt-3">
                            <a
                              href={info.mapLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-300 shadow-sm hover:shadow-md font-semibold text-sm space-x-2"
                              title="Open in Google Maps"
                            >
                              <Map className="h-4 w-4" />
                              <span>View on Google Maps</span>
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-12 p-6 bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Why Choose Us?</h3>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-700">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    good industry experience
                  </li>
                  <li className="flex items-center text-gray-700">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    10+ successful projects delivered
                  </li>
                  <li className="flex items-center text-gray-700">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    Rely on us for end to end delivery
                  </li>
                  <li className="flex items-center text-gray-700">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    Agile development methodology
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section data-id="faq-section" className="pt-8 pb-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 data-id="faq-title" className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p data-id="faq-description" className="text-xl text-gray-600">
              Get answers to common questions about our services and process.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                data-id={`faq-${index}`}
                className="bg-gray-50 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-500 transition-transform ${openFaq === index ? 'transform rotate-180' : ''
                      }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;